import { useEffect, useRef, useState } from 'react';

export interface GenerationProgressOptions {
  /** Cuánto tarda la barra en llenarse (ms). Ignorado si `indeterminate`. */
  durationMs: number;
  /** Tiempo con la barra quieta en 0% antes de empezar a llenarse (ms). */
  holdMs: number;
  /** Sin duración conocida: no hay motor, la barra la anima el CSS en loop. */
  indeterminate: boolean;
  /** El padre avisa que la generación terminó de verdad → salto rápido a 100%. */
  completeNow: boolean;
  /** La barra llega sola a 100% al cumplirse `durationMs` (sin el tope de 97%). */
  completeAtEnd: boolean;
  /**
   * Se llama UNA vez cuando la barra llega a 100%.
   * @param forzado `true` si llegó por `completeNow` (terminó el trabajo real),
   *   `false` si llegó sola por `completeAtEnd` (se cumplió el tiempo). El
   *   llamador usa esto para decidir si suena el "listo": un timer cumpliéndose
   *   no es una completitud real y no debería sonar.
   */
  onDone?: (forzado: boolean) => void;
}

/** Duración del salto forzado a 100% cuando llega `completeNow` (ms). */
const FORCED_COMPLETE_MS = 400;

/** Tope de la barra cuando NO hay `completeAtEnd`: nunca completa sola. */
const AUTO_CAP = 97;

/**
 * Pacing de la barra para una generación de IMÁGENES (ms): es lo que `InterfazV2`
 * le pasa a la pantalla de carga como `durationMs`, y el recuadro "Generando…" de
 * la tanda usa el MISMO número para que las dos barras avancen igual
 * (ADR-2026-09-06-nucleo-de-carga-compartido).
 */
export const DURACION_BARRA_IMAGEN_MS = 80_000;

/**
 * La fórmula de la barra "falsa", como función pura: cuánto tiene que marcar la
 * barra a los `elapsedMs` de una generación que se estima en `durationMs`.
 *
 * Easing `1 - (1-t)^1.5` (arranca rápido y frena al final, como una espera real
 * que "casi termina") y **tope 97%** salvo `completeAtEnd`: sin progreso real del
 * backend la barra no puede completar sola, porque una barra llena que sigue
 * esperando miente más que una en 97.
 *
 * Vive afuera del hook para que la use también el recuadro "Generando…" de la
 * tanda, que tickea con un `setInterval` y no con un RAF por card
 * (ADR-2026-09-06-nucleo-de-carga-compartido). Es la MISMA fórmula que corría
 * adentro del RAF: el hook la llama en su rama normal.
 */
export function progresoPaceado(
  elapsedMs: number,
  durationMs: number,
  completeAtEnd = false,
): number {
  const t = Math.min(1, Math.max(0, elapsedMs) / durationMs);
  const eased = 1 - Math.pow(1 - t, 1.5);
  const cap = completeAtEnd ? 100 : AUTO_CAP;
  return Math.min(eased * 100, cap);
}

/**
 * Motor de la barra de progreso de la pantalla de generación
 * (ADR-2026-08-02-pantalla-carga-unificada; comportamiento original en ADR-544/545).
 *
 * La barra es **falsa a propósito**: el backend no reporta porcentaje, así que se
 * anima con un easing `1 - (1-t)^1.5` sobre `durationMs` y **capea en 97%** —
 * nunca completa sola. El salto a 100% lo dispara el padre con `completeNow`
 * cuando la generación termina de verdad. Así el usuario nunca ve la barra llena
 * esperando, ni la ve trabada si la generación tarda más de lo estimado.
 *
 * Dos modos extra para el onboarding:
 * - `holdMs`: la barra arranca quieta en 0% (el "preparando" del ejemplo precargado).
 * - `indeterminate`: no hay motor; el que dibuja usa la animación CSS en loop.
 * - `completeAtEnd`: sí completa sola (el ejemplo precargado es puro timeline).
 *
 * @returns el progreso 0-100. En modo `indeterminate` siempre 0 (no se usa).
 */
export function useGenerationProgress({
  durationMs,
  holdMs,
  indeterminate,
  completeNow,
  completeAtEnd,
  onDone,
}: GenerationProgressOptions): number {
  const [progress, setProgress] = useState<number>(0);

  // Espejo en ref del progreso: lo lee el loop para saber desde dónde arrancar el
  // salto forzado. Antes se leía el state y eso obligaba a poner `progress` en las
  // deps del efecto → el RAF se re-creaba en CADA frame. Con el ref, el efecto se
  // monta una sola vez.
  const progressRef = useRef<number>(0);
  const completingRef = useRef<boolean>(false);
  const startTimeRef = useRef<number | null>(null);
  const completeStartRef = useRef<number | null>(null);
  const startProgressRef = useRef<number>(0);
  const doneRef = useRef<boolean>(false);
  const rafRef = useRef<number | null>(null);

  // Idem con el callback: los call-sites pasan arrows inline, que cambian de
  // identidad en cada render. Guardado en ref, el efecto no depende de él.
  const onDoneRef = useRef<GenerationProgressOptions['onDone']>(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    // Sin duración conocida no hay nada que calcular: la barra la anima el CSS.
    if (indeterminate) return;

    const write = (value: number) => {
      progressRef.current = value;
      setProgress(value);
    };

    const step = (now: number) => {
      if (doneRef.current) return;
      // `startTimeRef` NO se resetea entre re-montajes del efecto: si algo lo
      // vuelve a correr (StrictMode), el progreso continúa donde estaba en vez
      // de arrancar de cero.
      if (startTimeRef.current === null) startTimeRef.current = now;

      if (completingRef.current) {
        if (completeStartRef.current === null) {
          completeStartRef.current = now;
          startProgressRef.current = progressRef.current;
        }
        const t = Math.min(1, (now - completeStartRef.current) / FORCED_COMPLETE_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        const next = startProgressRef.current + (100 - startProgressRef.current) * eased;
        const clamped = next >= 99.9 ? 100 : next;
        write(clamped);
        if (clamped >= 100) {
          doneRef.current = true;
          onDoneRef.current?.(true);
          return;
        }
      } else {
        // Durante el `hold` la barra se queda en 0% ("preparando").
        const elapsed = now - startTimeRef.current - holdMs;
        if (elapsed <= 0) {
          write(0);
          rafRef.current = requestAnimationFrame(step);
          return;
        }
        const clamped = progresoPaceado(elapsed, durationMs, completeAtEnd);
        write(clamped);
        if (completeAtEnd && clamped >= 100) {
          doneRef.current = true;
          onDoneRef.current?.(false);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [durationMs, holdMs, indeterminate, completeAtEnd]);

  // El padre avisa que terminó → el loop pasa a la rama de salto forzado.
  useEffect(() => {
    if (!completeNow || doneRef.current) return;
    if (indeterminate) {
      // Sin barra determinada no hay a dónde animar: se cierra en el acto, para
      // que un flujo que use `indeterminate` + `completeNow` no quede colgado
      // esperando un `onDone` que nunca llegaría.
      doneRef.current = true;
      onDoneRef.current?.(true);
      return;
    }
    completingRef.current = true;
    completeStartRef.current = null; // reset del timing del salto
  }, [completeNow, indeterminate]);

  return progress;
}

/**
 * Progreso paceado desde un instante, tickeando solo. Es el motor de los recuadros
 * "Generando…" chicos: el de la tanda (`PanelTandaCreaciones`) y el de la card del
 * chat (`ChatGeneratingView`) — ADR-2026-09-08-la-espera-del-chat-usa-la-pantalla-de-carga.
 *
 * Tickea con un `setInterval` de 250 ms y no con un RAF: hasta 18 recuadros a 60 fps
 * con un `setState` por frame para un riel de 4 px es trabajo tirado, y la transición
 * de 300 ms del relleno alisa los saltos entre ticks
 * (ADR-2026-09-06-nucleo-de-carga-compartido).
 *
 * Vivía adentro de `PanelTandaCreaciones`; se subió acá cuando apareció el segundo
 * consumidor, en vez de copiarlo.
 */
export function useProgresoDesde(creadaEn: number, duracionMs: number): number {
  const [progreso, setProgreso] = useState(() =>
    progresoPaceado(Date.now() - creadaEn, duracionMs),
  );
  useEffect(() => {
    const tick = () =>
      setProgreso(progresoPaceado(Date.now() - creadaEn, duracionMs));
    tick();
    const t = window.setInterval(tick, 250);
    return () => window.clearInterval(t);
  }, [creadaEn, duracionMs]);
  return progreso;
}

export default useGenerationProgress;
